import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

/**
 * Provides platform-detection helpers that wrap Capacitor's runtime APIs.
 *
 * @remarks
 * Inject this service anywhere you need to branch logic based on the
 * current platform (native iOS, native Android, or web browser).
 *
 * @example
 * ```ts
 * constructor(private platform: PlatformService) {
 *   if (this.platform.isNative) { // show native UI }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PlatformService {
  private readonly _platform: string = Capacitor.getPlatform();

  /**
   * Returns `true` when the app is running on a native device (iOS or Android).
   *
   * @returns `true` for native platforms, `false` for web.
   */
  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Returns `true` when running on iOS.
   *
   * @returns `true` on iOS, `false` otherwise.
   */
  get isIOS(): boolean {
    return this._platform === 'ios';
  }

  /**
   * Returns `true` when running on Android.
   *
   * @returns `true` on Android, `false` otherwise.
   */
  get isAndroid(): boolean {
    return this._platform === 'android';
  }

  /**
   * Returns `true` when running in a web browser.
   *
   * @returns `true` on web, `false` otherwise.
   */
  get isWeb(): boolean {
    return this._platform === 'web';
  }

  /**
   * Returns the raw platform identifier string from Capacitor.
   *
   * @returns One of `'ios'`, `'android'`, or `'web'`.
   */
  getPlatform(): string {
    return this._platform;
  }
}
